import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/authcontext';
import {
  getBids,
  updateBid,
  deleteBid,
  getSubBids,
  createSubBid,
  updateSubBid,
  deleteSubBid,
  createBid
} from '../../services/api';
import {
  exportToCSV,
  exportToExcel,
  parseCSVFile,
  parseExcelFile,
  validateImportedBids,
  downloadSampleTemplate
} from '../../services/exportImportService';
import SubBidForm from '../subbidform/subbidform';
import AttachmentForm from '../attachmentform/attachmentform';
import BidForm from '../bidform/bidform';
import styles from './EnhancedBidTable.module.css';

function EnhancedBidTable({ bidView, defaultSort }) {
  const { user } = useAuth();
  const tableRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Utility function to get the appropriate CSS class for a status
  const getStatusClass = (status) => {
    if (!status) return `${styles.statusBadge} ${styles.open}`;

    const statusLower = status.toLowerCase();

    // Handle multi-word statuses
    if (statusLower === 'on hold') {
      return `${styles.statusBadge} ${styles.on}`;
    }

    // For single-word statuses, use the first word
    const firstWord = statusLower.split(' ')[0];
    return `${styles.statusBadge} ${styles[firstWord] || styles.open}`;
  };

  const [bids, setBids] = useState([]);
  const [sort, setSort] = useState(defaultSort || { key: '', order: '' });
  const [editingBid, setEditingBid] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subBids, setSubBids] = useState({});
  const [expandedBids, setExpandedBids] = useState({});
  const [showSubBidForm, setShowSubBidForm] = useState(null);
  const [editingSubBid, setEditingSubBid] = useState(null);
  const [editingSubBidData, setEditingSubBidData] = useState(null);
  const [loadingSubBids, setLoadingSubBids] = useState({});
  const [errorDetails, setErrorDetails] = useState(null);
  const [subBidSort, setSubBidSort] = useState({ key: '', order: '' });
  const [selectedTrades, setSelectedTrades] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: ''
  });

  // NEW: Enhanced features state
  const [selectedBids, setSelectedBids] = useState(new Set());
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('zenBidTableColumnWidths');
    return saved ? JSON.parse(saved) : {};
  });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('zenBidTableVisibleColumns');
    return saved ? JSON.parse(saved) : null;
  });
  const [rowDensity, setRowDensity] = useState(() => {
    return localStorage.getItem('zenBidTableRowDensity') || 'comfortable';
  });
  const [savedFilters, setSavedFilters] = useState(() => {
    const saved = localStorage.getItem('zenBidTableSavedFilters');
    return saved ? JSON.parse(saved) : [];
  });
  const [resizingColumn, setResizingColumn] = useState(null);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [inlineEdit, setInlineEdit] = useState({ bidId: null, field: null });

  // Status options for multi-select dropdown
  const statusOptions = [
    { value: 'Open', label: 'Open' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Awarded', label: 'Awarded' },
    { value: 'Lost', label: 'Lost' },
    { value: 'On Hold', label: 'On Hold' },
  ];

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showEditDropdown, setShowEditDropdown] = useState(null);
  const [showAttachmentForm, setShowAttachmentForm] = useState(null);
  const [showSubBidAttachmentForm, setShowSubBidAttachmentForm] = useState(null); // Track sub-bid attachment form
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Export/Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importValidation, setImportValidation] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Define available columns
  const allColumns = bidView === 'incoming' ? [
    { key: 'JobNumber', label: 'Project #', minWidth: 100, defaultWidth: 120 },
    { key: 'ClientName', label: 'Client', minWidth: 120, defaultWidth: 150 },
    { key: 'JobName', label: 'Project Name', minWidth: 150, defaultWidth: 200 },
    { key: 'BidAmount', label: 'Proposed Amount', minWidth: 120, defaultWidth: 140 },
    { key: 'totalBid', label: 'Sub Total Bids', minWidth: 120, defaultWidth: 140, calculated: true },
    { key: 'totalAwarded', label: 'Sub Total Awarded', minWidth: 120, defaultWidth: 160, calculated: true },
    { key: 'AssignedTo', label: 'Assigned', minWidth: 100, defaultWidth: 120 },
    { key: 'Contact', label: 'Contact', minWidth: 120, defaultWidth: 150 },
    { key: 'DateDue', label: 'Date Due', minWidth: 100, defaultWidth: 120 },
    { key: 'DateIssued', label: 'Date Issued', minWidth: 100, defaultWidth: 120 },
    { key: 'BidStatus', label: 'Status', minWidth: 120, defaultWidth: 140 },
  ] : [
    { key: 'JobNumber', label: 'Project #', minWidth: 100, defaultWidth: 120 },
    { key: 'ClientName', label: 'Client', minWidth: 120, defaultWidth: 150 },
    { key: 'JobName', label: 'Project Name', minWidth: 150, defaultWidth: 200 },
    { key: 'JobDescription', label: 'Description', minWidth: 150, defaultWidth: 200 },
    { key: 'BidAmount', label: 'Proposed Amount', minWidth: 120, defaultWidth: 140 },
    { key: 'AssignedTo', label: 'Assigned', minWidth: 100, defaultWidth: 120 },
    { key: 'Contact', label: 'Contact', minWidth: 120, defaultWidth: 150 },
    { key: 'DateDue', label: 'Date Due', minWidth: 100, defaultWidth: 120 },
    { key: 'DateIssued', label: 'Date Issued', minWidth: 100, defaultWidth: 120 },
    { key: 'BidStatus', label: 'Status', minWidth: 120, defaultWidth: 140 },
  ];

  // Initialize visible columns if not set
  useEffect(() => {
    if (!visibleColumns) {
      const defaultVisible = {};
      allColumns.forEach(col => {
        defaultVisible[col.key] = true;
      });
      setVisibleColumns(defaultVisible);
    }
  }, [allColumns, visibleColumns]);

  // Get currently visible columns
  const columns = useMemo(() => {
    if (!visibleColumns) return allColumns;
    return allColumns.filter(col => visibleColumns[col.key] !== false);
  }, [allColumns, visibleColumns]);

  // Save column widths to localStorage
  useEffect(() => {
    localStorage.setItem('zenBidTableColumnWidths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  // Save visible columns to localStorage
  useEffect(() => {
    if (visibleColumns) {
      localStorage.setItem('zenBidTableVisibleColumns', JSON.stringify(visibleColumns));
    }
  }, [visibleColumns]);

  // Save row density to localStorage
  useEffect(() => {
    localStorage.setItem('zenBidTableRowDensity', rowDensity);
  }, [rowDensity]);

  // Column resizing handlers
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn({ key: columnKey, startX: e.pageX });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizingColumn) return;

      const delta = e.pageX - resizingColumn.startX;
      const column = allColumns.find(col => col.key === resizingColumn.key);
      const currentWidth = columnWidths[resizingColumn.key] || column?.defaultWidth || 150;
      const newWidth = Math.max((column?.minWidth || 80), currentWidth + delta);

      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn.key]: newWidth
      }));

      setResizingColumn(prev => ({ ...prev, startX: e.pageX }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
    };

    if (resizingColumn) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn, columnWidths, allColumns]);

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedBids.size === sortedBids.length) {
      setSelectedBids(new Set());
    } else {
      setSelectedBids(new Set(sortedBids.map(bid => bid.id)));
    }
  };

  const toggleSelectBid = (bidId) => {
    setSelectedBids(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bidId)) {
        newSet.delete(bidId);
      } else {
        newSet.add(bidId);
      }
      return newSet;
    });
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedBids.size} bid(s)?`)) {
      return;
    }

    try {
      await Promise.all(Array.from(selectedBids).map(bidId => deleteBid(bidId)));
      fetchBids();
      setSelectedBids(new Set());
      setShowBulkActions(false);
    } catch (err) {
      console.error('Failed to delete bids:', err);
      setError('Failed to delete selected bids. Please try again.');
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    try {
      await Promise.all(
        Array.from(selectedBids).map(bidId =>
          updateBid(bidId, { BidStatus: newStatus })
        )
      );
      fetchBids();
      setSelectedBids(new Set());
      setShowBulkActions(false);
    } catch (err) {
      console.error('Failed to update bid statuses:', err);
      setError('Failed to update selected bids. Please try again.');
    }
  };

  // Toggle column visibility
  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Save filter preset
  const saveFilterPreset = (name) => {
    const preset = {
      id: Date.now(),
      name,
      filters: { ...filters },
      selectedStatuses: [...selectedStatuses]
    };
    setSavedFilters(prev => {
      const updated = [...prev, preset];
      localStorage.setItem('zenBidTableSavedFilters', JSON.stringify(updated));
      return updated;
    });
  };

  // Load filter preset
  const loadFilterPreset = (preset) => {
    setFilters(preset.filters);
    setSelectedStatuses(preset.selectedStatuses);
  };

  // Delete filter preset
  const deleteFilterPreset = (presetId) => {
    setSavedFilters(prev => {
      const updated = prev.filter(p => p.id !== presetId);
      localStorage.setItem('zenBidTableSavedFilters', JSON.stringify(updated));
      return updated;
    });
  };

  // Rest of the existing functions from the original BidTable
  const fetchBids = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorDetails(null);
    try {
      const fetchedBids = await getBids(user.uid);

      const filteredBids = fetchedBids.filter(bid => {
        if (bidView === 'incoming') {
          return bid.type === 'project' ||
                 bid.type === 'incoming' ||
                 (bid.JobNumber && !bid.type);
        } else {
          return bid.type === 'standalone';
        }
      });

      setBids(filteredBids);
    } catch (err) {
      console.error('BidTable: Failed to fetch bids:', err);
      setError('Failed to load bids. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [user, bidView]);

  useEffect(() => {
    if (defaultSort && defaultSort.key) {
      setSort(defaultSort);
    }
  }, [defaultSort]);

  useEffect(() => {
    if (user) {
      fetchBids();
    }
  }, [user, bidView, fetchBids]);

  useEffect(() => {
    if (bidView === 'incoming' && bids.length > 0) {
      const fetchAllSubBids = async () => {
        const bidQueue = [...bids];
        const batchSize = 5;

        while (bidQueue.length > 0) {
          const batch = bidQueue.splice(0, batchSize);

          await Promise.all(
            batch.map(async (bid) => {
              if (!subBids[bid.id] || subBids[bid.id].length === 0) {
                try {
                  const fetchedSubBids = await getSubBids(bid.id);

                  setSubBids(prev => ({
                    ...prev,
                    [bid.id]: fetchedSubBids
                  }));
                } catch (err) {
                  console.error(`BidTable: Failed to fetch sub-bids for bid ${bid.id}:`, err);
                }
              }
            })
          );

          if (bidQueue.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      };

      fetchAllSubBids();
    }
  }, [bidView, bids]);

  const handleSort = (key) => {
    const order = sort.key === key && sort.order === 'asc' ? 'desc' : 'asc';
    setSort({ key, order });
  };

  const getSortArrow = (col) => {
    if (sort.key === col) {
      return sort.order === 'asc' ? ' ▼' : ' ▲';
    }
    return '';
  };

  const handleSubBidSort = (key) => {
    const order = subBidSort.key === key && subBidSort.order === 'asc' ? 'desc' : 'asc';
    setSubBidSort({ key, order });
  };

  const getSubBidSortArrow = (col) => {
    if (subBidSort.key === col) {
      return subBidSort.order === 'asc'
        ? ' ▼'
        : ' ▲';
    }
    return '';
  };

  const handleEdit = (bid) => {
    setSelectedBid(bid);
    setShowEditForm(true);
    setShowEditDropdown(null);
  };

  const handleUpdateBid = async (updatedData) => {
    setError(null);
    setErrorDetails(null);
    setIsSubmitting(true);
    try {
      await updateBid(selectedBid.id, updatedData);
      setBids(prevBids => {
        const updated = prevBids.map(b => b.id === selectedBid.id ? { ...b, ...updatedData } : b);
        return [...updated];
      });
      setShowEditForm(false);
      setSelectedBid(null);
    } catch (err) {
      console.error('Failed to update bid:', err);
      setError('Failed to update bid. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (bidId) => {
    if (window.confirm('Are you sure you want to delete this bid?')) {
      setError(null);
      setErrorDetails(null);
      try {
        await deleteBid(bidId);
        fetchBids();
      } catch (err) {
        console.error('Failed to delete bid:', err);
        setError('Failed to delete bid. Please try again later.');
        setErrorDetails(err.message || 'Unknown error occurred');
      }
    }
  };

  const toggleExpand = async (bidId) => {
    const newExpandedState = !expandedBids[bidId];
    setExpandedBids(prev => ({
      ...prev,
      [bidId]: newExpandedState
    }));

    if (newExpandedState && (!subBids[bidId] || subBids[bidId].length === 0)) {
      await fetchSubBids(bidId);
    }

    if (newExpandedState && !selectedTrades[bidId]) {
      setSelectedTrades(prev => ({
        ...prev,
        [bidId]: 'All'
      }));
    }
  };

  const fetchSubBids = async (bidId) => {
    setLoadingSubBids(prev => ({ ...prev, [bidId]: true }));
    setError(null);
    setErrorDetails(null);

    try {
      const fetchedSubBids = await getSubBids(bidId);

      setSubBids(prev => ({
        ...prev,
        [bidId]: fetchedSubBids
      }));
    } catch (err) {
      console.error('Failed to fetch sub-bids:', err);
      setError(`Failed to load sub-bids for bid ${bidId}. Please try again later.`);
      setErrorDetails(err.message || 'Unknown error occurred');
    } finally {
      setLoadingSubBids(prev => ({ ...prev, [bidId]: false }));
    }
  };

  const handleCreateSubBid = async (subBidData) => {
    if (!showSubBidForm) {
      console.error('Cannot create sub-bid - no bid ID specified');
      return;
    }

    setError(null);
    setErrorDetails(null);

    try {
      await createSubBid(showSubBidForm, subBidData);
      await fetchSubBids(showSubBidForm);
      setShowSubBidForm(null);
    } catch (err) {
      console.error('Failed to create sub-bid:', err);
      setError('Failed to create sub-bid. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    }
  };

  const handleEditSubBid = (bidId, subBid) => {
    setEditingSubBid(subBid.id);
    setEditingSubBidData(subBid);
  };

  const handleUpdateSubBid = async (updatedData) => {
    if (!editingSubBid || !editingSubBidData) {
      console.error('Cannot update sub-bid - missing subBid ID or data');
      return;
    }

    const bidId = Object.keys(subBids).find(
      bidId => subBids[bidId]?.some(subBid => subBid.id === editingSubBid)
    );

    if (!bidId) {
      console.error('Could not find parent bid for sub-bid:', editingSubBid);
      return;
    }

    setError(null);
    setErrorDetails(null);

    try {
      await updateSubBid(bidId, editingSubBid, updatedData);
      await fetchSubBids(bidId);
      setEditingSubBid(null);
      setEditingSubBidData(null);
    } catch (err) {
      console.error('Failed to update sub-bid:', err);
      setError('Failed to update sub-bid. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    }
  };

  const handleDeleteSubBid = async (bidId, subBidId) => {
    if (window.confirm('Are you sure you want to delete this sub-bid?')) {
      setError(null);
      setErrorDetails(null);

      try {
        await deleteSubBid(bidId, subBidId);
        await fetchSubBids(bidId);
      } catch (err) {
        console.error('Failed to delete sub-bid:', err);
        setError('Failed to delete sub-bid. Please try again later.');
        setErrorDetails(err.message || 'Unknown error occurred');
      }
    }
  };

  const calculateTotalBidAmount = (bidId) => {
    if (!subBids[bidId] || subBids[bidId].length === 0) {
      return 0;
    }

    return subBids[bidId].reduce((total, subBid) => {
      return total + (parseFloat(subBid.SubBidAmount) || 0);
    }, 0);
  };

  const calculateTotalAwardedAmount = (bidId) => {
    if (!subBids[bidId] || subBids[bidId].length === 0) {
      return 0;
    }

    return subBids[bidId].reduce((total, subBid) => {
      return total + (parseFloat(subBid.AwardedAmount) || 0);
    }, 0);
  };

  const subBidColumns = [
    { key: 'SubcontractorName', label: 'Subcontractor' },
    { key: 'Trade', label: 'Trade' },
    { key: 'SubBidAmount', label: 'Bid Amount' },
    { key: 'AwardedAmount', label: 'Awarded Amount' },
    { key: 'Contact', label: 'Contact' },
  ];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const toggleStatusSelection = (statusValue) => {
    setSelectedStatuses(prev => {
      const isSelected = prev.some(s => s.value === statusValue);
      if (isSelected) {
        return prev.filter(s => s.value !== statusValue);
      } else {
        const option = statusOptions.find(opt => opt.value === statusValue);
        return [...prev, option];
      }
    });
  };

  const filteredBids = useMemo(() => {
    return [...bids].filter(bid => {
      if (selectedStatuses.length > 0 &&
          !selectedStatuses.some(status => status.value === bid.BidStatus)) {
        return false;
      }

      if (filters.assignedTo && bid.AssignedTo !== filters.assignedTo) {
        return false;
      }

      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matches = !searchTerm ||
          bid.ProjectNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bid.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bid.ClientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bid.JobName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bid.AssignedTo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bid.Contact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          bid.BidAmount?.toString().includes(searchTerm) ||
          bid.BidStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (bid.DateDue && new Date(bid.DateDue).toLocaleDateString().toLowerCase().includes(searchTerm)) ||
          (bid.DateIssued && new Date(bid.DateIssued).toLocaleDateString().toLowerCase().includes(searchTerm));
        return matches;
      }

      return true;
    });
  }, [bids, filters, selectedStatuses]);

  const sortedBids = useMemo(() => {
    return [...filteredBids].sort((a, b) => {
      if (!sort.key) return 0;

      if (sort.key === 'totalBid') {
        const aTotal = calculateTotalBidAmount(a.id);
        const bTotal = calculateTotalBidAmount(b.id);
        return sort.order === 'asc' ? aTotal - bTotal : bTotal - aTotal;
      }

      if (sort.key === 'totalAwarded') {
        const aTotal = calculateTotalAwardedAmount(a.id);
        const bTotal = calculateTotalAwardedAmount(b.id);
        return sort.order === 'asc' ? aTotal - bTotal : bTotal - aTotal;
      }

      const aValue = a[sort.key];
      const bValue = b[sort.key];

      if (sort.key === 'DateIssued' || sort.key === 'DateDue') {
        const aDate = aValue ? new Date(aValue).getTime() : 0;
        const bDate = bValue ? new Date(bValue).getTime() : 0;
        return sort.order === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sort.order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aString = String(aValue || '').toLowerCase();
      const bString = String(bValue || '').toLowerCase();

      if (sort.order === 'asc') {
        return aString.localeCompare(bString);
      } else {
        return bString.localeCompare(aString);
      }
    });
  }, [filteredBids, sort]);

  const toggleEditDropdown = (bidId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();

      // Calculate dropdown position based on button position
      // For fixed positioning, use viewport coordinates (no scroll offset needed)
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px margin below button
        left: rect.right - 200 // 200px is dropdown width, align to right edge of button
      });
    }

    setShowEditDropdown(prev => prev === bidId ? null : bidId);
    if (showEditDropdown !== bidId) {
      setShowAttachmentForm(null);
    }
  };

  const handleDuplicateBid = async (bid) => {
    if (!user) {
      setError('You must be logged in to duplicate a bid');
      return;
    }

    setError(null);
    setErrorDetails(null);

    try {
      const { id, totalBid, totalAwarded, attachments, ...bidData } = bid;

      await createBid(user.uid, {
        ...bidData,
        createdAt: new Date().toISOString(),
        DateIssued: new Date().toISOString().split('T')[0]
      });

      fetchBids();
      setShowEditDropdown(null);
    } catch (err) {
      console.error('Failed to duplicate bid:', err);
      setError('Failed to duplicate bid. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    }
  };

  const handleAddAttachment = (bid) => {
    setShowAttachmentForm(bid);
    setShowEditDropdown(null);
  };

  const handleAttachmentSubmit = async (attachmentData) => {
    if (!showAttachmentForm) return;

    setError(null);
    setErrorDetails(null);

    try {
      await updateBid(showAttachmentForm.id, {
        attachments: attachmentData
      });

      setShowAttachmentForm(null);
      await fetchBids();
    } catch (err) {
      console.error('Failed to add attachment:', err);
      setError('Failed to add attachment. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    }
  };

  const handleOpenAttachment = (url) => {
    if (url) {
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
      window.open(formattedUrl, '_blank');
    }
  };

  // Handle sub-bid attachment
  const handleAddSubBidAttachment = (bidId, subBid) => {
    setShowSubBidAttachmentForm({ bidId, subBid });
  };

  const handleSubBidAttachmentSubmit = async (attachmentData) => {
    if (!showSubBidAttachmentForm) return;

    const { bidId, subBid } = showSubBidAttachmentForm;
    setError(null);
    setErrorDetails(null);

    try {
      await updateSubBid(bidId, subBid.id, {
        attachments: attachmentData
      });

      setShowSubBidAttachmentForm(null);
      // Refresh sub-bids for this bid
      const fetchedSubBids = await getSubBids(bidId);
      setSubBids(prev => ({
        ...prev,
        [bidId]: fetchedSubBids
      }));
    } catch (err) {
      console.error('Failed to add sub-bid attachment:', err);
      setError('Failed to add attachment. Please try again later.');
      setErrorDetails(err.message || 'Unknown error occurred');
    }
  };

  const handleAddSubBid = (bidId) => {
    setShowSubBidForm(bidId);
    setShowEditDropdown(null);

    if (!expandedBids[bidId]) {
      toggleExpand(bidId);
    }
  };

  // Export handlers
  const handleExportExcel = async () => {
    try {
      await exportToExcel(sortedBids, 'zenbid_export', bidView);
    } catch (err) {
      console.error('Failed to export Excel:', err);
      setError('Failed to export Excel. Please try again.');
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadSampleTemplate(bidView);
    } catch (err) {
      console.error('Failed to download template:', err);
      setError('Failed to download template. Please try again.');
    }
  };

  // Import handlers
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError(null);

    try {
      let parsedBids;

      if (file.name.endsWith('.csv')) {
        parsedBids = await parseCSVFile(file, bidView);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        parsedBids = await parseExcelFile(file, bidView);
      } else {
        throw new Error('Invalid file type. Please upload a CSV or Excel file.');
      }

      // Validate imported bids
      const validation = validateImportedBids(parsedBids);
      setImportValidation(validation);
      setShowImportModal(true);
    } catch (err) {
      console.error('Failed to import file:', err);
      setError(`Import failed: ${err.message}`);
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmImport = async () => {
    if (!importValidation || !importValidation.valid || importValidation.validatedBids.length === 0) {
      return;
    }

    setIsImporting(true);
    setError(null);

    try {
      // Create all validated bids
      await Promise.all(
        importValidation.validatedBids.map(bidData =>
          createBid(user.uid, {
            ...bidData,
            createdAt: new Date().toISOString()
          })
        )
      );

      // Refresh bids list
      await fetchBids();
      setShowImportModal(false);
      setImportValidation(null);

      // Show success message
      alert(`Successfully imported ${importValidation.validatedBids.length} bid(s)!`);
    } catch (err) {
      console.error('Failed to import bids:', err);
      setError(`Failed to import bids: ${err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancelImport = () => {
    setShowImportModal(false);
    setImportValidation(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEditDropdown !== null && dropdownRef.current) {
        // Check if click is inside the dropdown or the button that opened it
        const isInsideDropdown = dropdownRef.current.contains(event.target);
        const isDropdownButton = event.target.closest(`.${styles.editDropdownButton}`);

        if (!isInsideDropdown && !isDropdownButton) {
          setShowEditDropdown(null);
        }
      }
    };

    // Delay attaching the listener to avoid catching the click that opened the dropdown
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEditDropdown, styles.editDropdownButton]);

  // Close column and status selectors when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const columnSelectorWrapper = event.target.closest(`.${styles.columnSelectorWrapper}`);
      const statusSelectorWrapper = event.target.closest(`.${styles.statusSelectorWrapper}`);

      if (!columnSelectorWrapper && showColumnSelector) {
        setShowColumnSelector(false);
      }
      if (!statusSelectorWrapper && showStatusSelector) {
        setShowStatusSelector(false);
      }
    };

    if (showColumnSelector || showStatusSelector) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSelector, showStatusSelector, styles.columnSelectorWrapper, styles.statusSelectorWrapper]);

  if (loading) {
    return <div className={styles.loading}>Loading bids...</div>;
  }

  if (error) {
    return (
      <div className={styles.error}>
        <div>{error}</div>
        {errorDetails && (
          <div className={styles.errorDetails}>
            <p>Error details: {errorDetails}</p>
            {errorDetails.includes('permission') && (
              <p>
                This may be due to insufficient Firestore permissions. Please make sure
                you have updated the security rules in the Firebase Console to allow
                access to the subbids subcollection.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  if (bids.length === 0) {
    return <div className={styles.empty}>No bids found. Create a new bid to get started.</div>;
  }

  return (
    <div className={styles.bidTableContainer}>
      {/* Bulk selection toolbar */}
      {selectedBids.size > 0 && (
        <div className={styles.bulkSelectionBar}>
          <span>{selectedBids.size} selected</span>
          <button
            onClick={() => setShowBulkActions(!showBulkActions)}
            className={styles.bulkActionsButton}
          >
            Bulk Actions ▼
          </button>
          {showBulkActions && (
            <div className={styles.bulkActionsMenu}>
              <button onClick={() => handleBulkStatusUpdate('Awarded')}>
                Mark as Awarded
              </button>
              <button onClick={() => handleBulkStatusUpdate('Lost')}>
                Mark as Lost
              </button>
              <button onClick={() => handleBulkStatusUpdate('On Hold')}>
                Mark as On Hold
              </button>
              <button
                onClick={handleBulkDelete}
                className={styles.bulkDeleteButton}
              >
                Delete Selected
              </button>
            </div>
          )}
          <button
            onClick={() => setSelectedBids(new Set())}
            className={styles.clearSelectionButton}
          >
            Clear
          </button>
        </div>
      )}

      {/* Filters & Controls - All in one compact row */}
      <div className={styles.filtersContainer}>
        <input
          type="text"
          name="search"
          value={filters.search}
          onChange={handleFilterChange}
          placeholder="Search..."
          className={styles.searchInput}
        />

        <div className={styles.statusSelectorWrapper}>
          <button
            onClick={() => setShowStatusSelector(!showStatusSelector)}
            className={styles.statusSelectorButton}
          >
            Filter by Status ▼
          </button>
          {showStatusSelector && (
            <div className={styles.statusSelectorMenu}>
              {statusOptions.map(option => (
                <label key={option.value} className={styles.statusOption}>
                  <input
                    type="checkbox"
                    checked={selectedStatuses.some(s => s.value === option.value)}
                    onChange={() => toggleStatusSelection(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <select
          name="assignedTo"
          value={filters.assignedTo || ''}
          onChange={handleFilterChange}
          className={styles.statusFilter}
          aria-label="Filter by Assigned To"
        >
          <option value="">All Assigned</option>
          {[...new Set(bids.map(bid => bid.AssignedTo).filter(Boolean))].map(assignedTo => (
            <option key={assignedTo} value={assignedTo}>{assignedTo}</option>
          ))}
        </select>

        {/* Saved filter presets */}
        {savedFilters.length > 0 && (
          <select
            onChange={(e) => {
              const preset = savedFilters.find(p => p.id === parseInt(e.target.value));
              if (preset) loadFilterPreset(preset);
            }}
            className={styles.presetSelector}
          >
            <option value="">Load Filter Preset...</option>
            {savedFilters.map(preset => (
              <option key={preset.id} value={preset.id}>{preset.name}</option>
            ))}
          </select>
        )}

        {/* Divider */}
        <div className={styles.filterDivider}></div>

        {/* Row density selector */}
        <select
          value={rowDensity}
          onChange={(e) => setRowDensity(e.target.value)}
          className={styles.densitySelector}
        >
          <option value="compact">Compact</option>
          <option value="comfortable">Comfortable</option>
          <option value="spacious">Spacious</option>
        </select>

        {/* Column selector */}
        <div className={styles.columnSelectorWrapper}>
          <button
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className={styles.columnSelectorButton}
          >
            Columns ▼
          </button>
          {showColumnSelector && (
            <div className={styles.columnSelectorMenu}>
              {allColumns.map(col => (
                <label key={col.key} className={styles.columnOption}>
                  <input
                    type="checkbox"
                    checked={visibleColumns?.[col.key] !== false}
                    onChange={() => toggleColumnVisibility(col.key)}
                  />
                  <span>{col.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className={styles.filterDivider}></div>

        {/* Export button */}
        <button
          onClick={handleExportExcel}
          className={styles.exportButton}
          disabled={sortedBids.length === 0}
        >
          Export to Excel
        </button>

        {/* Import button */}
        <button
          onClick={handleImportClick}
          className={styles.importButton}
          disabled={isImporting}
        >
          {isImporting ? 'Importing...' : 'Import'}
        </button>

        {/* Download Template button */}
        <button
          onClick={handleDownloadTemplate}
          className={styles.templateButton}
        >
          Download Template
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Table with wrapper for width constraint */}
      <div className={styles.tableWrapper}>
        <table className={`${styles.table} ${styles[rowDensity]}`} ref={tableRef}>
          <thead className={styles.stickyHeader}>
            <tr>
              {/* Bulk select checkbox */}
              <th className={styles.checkboxColumn}>
                <input
                  type="checkbox"
                  checked={selectedBids.size === sortedBids.length && sortedBids.length > 0}
                  onChange={toggleSelectAll}
                  aria-label="Select all bids"
                />
              </th>

              {/* Expansion column for project bids */}
              {bidView === 'incoming' && <th className={styles.expandColumn}></th>}

              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={(e) => {
                    // Don't sort if clicking on resize handle
                    if (e.target.hasAttribute('data-resize-handle')) {
                      return;
                    }
                    handleSort(col.key);
                  }}
                  style={{
                    width: columnWidths[col.key] || col.defaultWidth,
                    minWidth: col.minWidth,
                    position: 'relative'
                  }}
                  className={styles.resizableHeader}
                >
                  <span className={styles.headerContent}>
                    {col.label} {getSortArrow(col.key)}
                  </span>
                  <div
                    data-resize-handle="true"
                    className={styles.resizeHandle}
                    onMouseDown={(e) => handleMouseDown(e, col.key)}
                    onClick={(e) => e.stopPropagation()}
                    title="Drag to resize column"
                  />
                </th>
              ))}
              <th className={styles.actionsColumn}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBids.map(bid => (
              <React.Fragment key={bid.id}>
                <tr className={selectedBids.has(bid.id) ? styles.selectedRow : ''}>
                  {/* Checkbox */}
                  <td className={styles.checkboxColumn}>
                    <input
                      type="checkbox"
                      checked={selectedBids.has(bid.id)}
                      onChange={() => toggleSelectBid(bid.id)}
                      aria-label={`Select bid ${bid.JobNumber || bid.ClientName}`}
                    />
                  </td>

                  {/* Expansion button for project bids */}
                  {bidView === 'incoming' && (
                    <td className={styles.expandColumn}>
                      <button
                        className={styles.expandButton}
                        onClick={() => toggleExpand(bid.id)}
                        aria-label={expandedBids[bid.id] ? "Collapse" : "Expand"}
                      >
                        {expandedBids[bid.id] ? '▼' : '▶'}
                      </button>
                    </td>
                  )}

                  {columns.map(col => (
                    <td key={col.key}>
                      {col.key === 'BidStatus' ? (
                        <select
                          value={bid.BidStatus || 'Open'}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await updateBid(bid.id, { BidStatus: newStatus });
                              setBids(prevBids => {
                                const updated = prevBids.map(b => b.id === bid.id ? { ...b, BidStatus: newStatus } : b);
                                if (selectedBid && selectedBid.id === bid.id) {
                                  setSelectedBid(prev => prev ? { ...prev, BidStatus: newStatus } : prev);
                                }
                                return [...updated];
                              });
                            } catch (err) {
                              alert('Failed to update status: ' + (err.message || err));
                            }
                          }}
                          className={getStatusClass(bid.BidStatus)}
                          style={{ minWidth: 120 }}
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      ) : col.key === 'totalBid' ? (
                        `$${calculateTotalBidAmount(bid.id).toLocaleString()}`
                      ) : col.key === 'totalAwarded' ? (
                        `$${calculateTotalAwardedAmount(bid.id).toLocaleString()}`
                      ) : col.key === 'BidAmount' ? (
                        bid[col.key] ? `$${Number(bid[col.key]).toLocaleString()}` : '$0'
                      ) : col.key === 'AssignedTo' ? (
                        bid[col.key] || '-'
                      ) : col.key === 'Contact' ? (
                        bid[col.key] || '-'
                      ) : col.key === 'DateDue' ? (
                        bid[col.key] ? new Date(bid.DateDue).toLocaleDateString() : '-'
                      ) : col.key === 'DateIssued' ? (
                        bid[col.key] ? new Date(bid.DateIssued).toLocaleDateString() : '-'
                      ) : (
                        bid[col.key]
                      )}
                    </td>
                  ))}

                  <td className={styles.actions}>
                    <div className={styles.actionButtons}>
                      <div className={styles.dropdownContainer}>
                        <button
                          onClick={(event) => toggleEditDropdown(bid.id, event)}
                          className={styles.editDropdownButton}
                        >
                          <span>Edit</span>
                          <span>▼</span>
                        </button>

                        {showEditDropdown === bid.id && createPortal(
                          <div
                            className={styles.editDropdown}
                            ref={dropdownRef}
                            style={{
                              position: 'fixed',
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`,
                              zIndex: 9999
                            }}
                          >
                            <button onClick={() => handleEdit(bid)}>
                              Update Project
                            </button>
                            <button onClick={() => handleDuplicateBid(bid)}>
                              Duplicate Project
                            </button>
                            {bidView === 'incoming' && (
                              <button onClick={() => handleAddSubBid(bid.id)}>
                                Add Sub-Bid
                              </button>
                            )}
                            <button onClick={() => handleAddAttachment(bid)}>
                              Attachment
                            </button>
                            <button
                              onClick={() => handleDelete(bid.id)}
                              className={styles.deleteAction}
                            >
                              Delete Project
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>

                      {/* Attachment icons */}
                      {bid.attachments && (
                        <div className={styles.attachmentIcons}>
                          {bid.attachments.url && (
                            <button
                              onClick={() => handleOpenAttachment(bid.attachments.url)}
                              className={styles.attachmentIcon}
                              title="Open URL attachment"
                            >
                              ⚡
                            </button>
                          )}
                          {bid.attachments.fileUrl && (
                            <button
                              onClick={() => handleOpenAttachment(bid.attachments.fileUrl)}
                              className={styles.attachmentIcon}
                              title="Open file attachment"
                            >
                              📄
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Sub-bids section (expandable for project bids) */}
                {expandedBids[bid.id] && bidView === 'incoming' && (
                  <tr>
                    <td colSpan={columns.length + 3} className={styles.subBidCell}>
                      {loadingSubBids[bid.id] ? (
                        <div className={styles.loading}>Loading sub-bids...</div>
                      ) : (
                        <div className={styles.subBidSection}>
                          <div className={styles.subBidHeader}>
                            <h4>Sub-Bids</h4>
                            {subBids[bid.id] && subBids[bid.id].length > 0 && (
                              <div>
                                <select
                                  className={styles.tradeFilter}
                                  value={selectedTrades[bid.id] || 'All'}
                                  onChange={(e) => setSelectedTrades({ ...selectedTrades, [bid.id]: e.target.value })}
                                >
                                  <option value="All">All Trades</option>
                                  {[...new Set(subBids[bid.id].map(subBid => subBid.Trade))].map(trade => (
                                    <option key={trade} value={trade}>{trade}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          {(!subBids[bid.id] || subBids[bid.id].length === 0) ? (
                            <div className={styles.empty}>No sub-bids found. Add a sub-bid to get started.</div>
                          ) : (
                            <table className={styles.subTable}>
                              <thead>
                                <tr>
                                  {subBidColumns.map(col => (
                                    <th
                                      key={col.key}
                                      onClick={() => handleSubBidSort(col.key)}
                                    >
                                      {col.label} {getSubBidSortArrow(col.key)}
                                    </th>
                                  ))}
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subBids[bid.id]
                                  .filter(subBid => selectedTrades[bid.id] === 'All' || subBid.Trade === selectedTrades[bid.id])
                                  .sort((a, b) => {
                                    if (!subBidSort.key) return 0;

                                    if (subBidSort.key === 'SubBidAmount' || subBidSort.key === 'AwardedAmount') {
                                      const aVal = parseFloat(a[subBidSort.key]) || 0;
                                      const bVal = parseFloat(b[subBidSort.key]) || 0;
                                      return subBidSort.order === 'asc' ? aVal - bVal : bVal - aVal;
                                    }

                                    const aVal = String(a[subBidSort.key] || '').toLowerCase();
                                    const bVal = String(b[subBidSort.key] || '').toLowerCase();

                                    return subBidSort.order === 'asc'
                                      ? aVal.localeCompare(bVal)
                                      : bVal.localeCompare(aVal);
                                  })
                                  .map(subBid => {
                                    const isAwarded = parseFloat(subBid.AwardedAmount) > 0;

                                    return (
                                      <React.Fragment key={subBid.id}>
                                        <tr className={isAwarded ? styles.awardedRow : ''}>
                                          {subBidColumns.map(col => (
                                            <td key={col.key}>
                                              {col.key === 'SubBidAmount' ? (
                                                `$${Number(subBid.SubBidAmount).toLocaleString()}`
                                              ) : col.key === 'AwardedAmount' ? (
                                                `$${Number(subBid.AwardedAmount).toLocaleString()}`
                                              ) : col.key === 'Contact' ? (
                                                subBid[col.key] || '-'
                                              ) : (
                                                subBid[col.key]
                                              )}
                                            </td>
                                          ))}
                                          <td className={styles.actions}>
                                            <button
                                              onClick={() => handleEditSubBid(bid.id, subBid)}
                                              className={styles.editButton}
                                            >
                                              Edit
                                            </button>
                                            <button
                                              onClick={() => handleAddSubBidAttachment(bid.id, subBid)}
                                              className={styles.attachmentButton}
                                            >
                                              Attachments
                                            </button>
                                            <button
                                              onClick={() => handleDeleteSubBid(bid.id, subBid.id)}
                                              className={styles.deleteButton}
                                            >
                                              Delete
                                            </button>
                                            {subBid.attachments && (
                                              <span className={styles.attachmentIcons}>
                                                {subBid.attachments.url && (
                                                  <button
                                                    onClick={() => handleOpenAttachment(subBid.attachments.url)}
                                                    className={styles.attachmentIcon}
                                                    title="Open URL attachment"
                                                  >
                                                    ⚡
                                                  </button>
                                                )}
                                                {subBid.attachments.fileUrl && (
                                                  <button
                                                    onClick={() => handleOpenAttachment(subBid.attachments.fileUrl)}
                                                    className={styles.attachmentIcon}
                                                    title="Open file attachment"
                                                  >
                                                    📄
                                                  </button>
                                                )}
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      </React.Fragment>
                                    );
                                  })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit form modal */}
      {showEditForm && selectedBid && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <BidForm
              bid={selectedBid}
              bidType={selectedBid.type === 'project' ? 'project' : 'standalone'}
              onSubmit={handleUpdateBid}
              onCancel={() => {
                setShowEditForm(false);
                setSelectedBid(null);
              }}
              isSubmitting={isSubmitting}
              isModal={true}
            />
          </div>
        </div>
      )}

      {/* Add Sub-Bid modal */}
      {showSubBidForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.subBidModalHeader}>
              <h3>Add New Sub-Bid</h3>
            </div>
            <SubBidForm
              onSubmit={handleCreateSubBid}
              onCancel={() => setShowSubBidForm(null)}
            />
          </div>
        </div>
      )}

      {/* Edit Sub-Bid modal */}
      {editingSubBid && editingSubBidData && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.subBidModalHeader}>
              <h3>Edit Sub-Bid</h3>
            </div>
            <SubBidForm
              onSubmit={handleUpdateSubBid}
              onCancel={() => {
                setEditingSubBid(null);
                setEditingSubBidData(null);
              }}
              defaultValues={editingSubBidData}
            />
          </div>
        </div>
      )}

      {/* Bid Attachment modal */}
      {showAttachmentForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.subBidModalHeader}>
              <h3>Manage Attachments - {showAttachmentForm.JobName || showAttachmentForm.ClientName || 'Bid'}</h3>
            </div>
            <AttachmentForm
              onAttachmentAdded={handleAttachmentSubmit}
              onCancel={() => setShowAttachmentForm(null)}
              existingAttachments={showAttachmentForm.attachments}
              bidId={showAttachmentForm.id}
            />
          </div>
        </div>
      )}

      {/* Sub-Bid Attachment modal */}
      {showSubBidAttachmentForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.subBidModalHeader}>
              <h3>Manage Attachments - {showSubBidAttachmentForm.subBid.SubcontractorName || 'Sub-Bid'}</h3>
            </div>
            <AttachmentForm
              onAttachmentAdded={handleSubBidAttachmentSubmit}
              onCancel={() => setShowSubBidAttachmentForm(null)}
              existingAttachments={showSubBidAttachmentForm.subBid.attachments}
              bidId={showSubBidAttachmentForm.subBid.id}
            />
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {showImportModal && importValidation && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.importModal}>
              <h2>Import Preview</h2>

              {importValidation.valid ? (
                <div className={styles.importSuccess}>
                  <p className={styles.successMessage}>
                    ✓ All {importValidation.totalRows} row(s) validated successfully!
                  </p>
                  <p>Ready to import {importValidation.validRows} bid(s).</p>
                </div>
              ) : (
                <div className={styles.importWarning}>
                  <p className={styles.warningMessage}>
                    ⚠ Found {importValidation.errorRows} error(s) in {importValidation.totalRows} row(s)
                  </p>
                  <p>{importValidation.validRows} bid(s) can be imported.</p>

                  <div className={styles.errorList}>
                    <h4>Errors:</h4>
                    {importValidation.errors.map((error, idx) => (
                      <div key={idx} className={styles.errorItem}>
                        <strong>Row {error.row}:</strong>
                        <ul>
                          {error.errors.map((errMsg, errIdx) => (
                            <li key={errIdx}>{errMsg}</li>
                          ))}
                        </ul>
                        <details className={styles.errorDetails}>
                          <summary>Show row data</summary>
                          <pre>{JSON.stringify(error.data, null, 2)}</pre>
                        </details>
                      </div>
                    ))}
                  </div>

                  {importValidation.validRows > 0 && (
                    <p className={styles.importNote}>
                      Note: Only valid rows will be imported. Rows with errors will be skipped.
                    </p>
                  )}
                </div>
              )}

              <div className={styles.modalActions}>
                <button
                  onClick={handleCancelImport}
                  className={styles.cancelButton}
                  disabled={isImporting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmImport}
                  className={styles.confirmButton}
                  disabled={isImporting || importValidation.validRows === 0}
                >
                  {isImporting ? 'Importing...' : `Import ${importValidation.validRows} Bid(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnhancedBidTable;
